/**
 * Data Freshness Utilities
 *
 * Handles stale data indicators, offline detection, and cache management
 * for the Player Profile and other data-heavy pages.
 *
 * Per plan sections 2c (Offline/stale data support):
 * - Stale-while-revalidate pattern
 * - Offline detection
 * - Stale indicators
 */

// Data age thresholds in seconds
export const STALE_THRESHOLDS = {
  player_info: 7 * 24 * 60 * 60,  // 1 week
  trophies: 7 * 24 * 60 * 60,      // 1 week
  transfers: 7 * 24 * 60 * 60,     // 1 week
  injuries: 12 * 60 * 60,          // 12 hours
  percentiles: 24 * 60 * 60,       // 1 day
  season_stats: 12 * 60 * 60,      // 12 hours
} as const;

export type DataSection = keyof typeof STALE_THRESHOLDS;

/**
 * Metadata returned from API for data freshness
 */
export interface SectionMeta {
  is_stale?: boolean;
  data_age_seconds?: number | null;
  data_age?: string;
  fetch_error?: string;
  fetched_from_api?: boolean;
}

export interface ResponseMeta {
  player_id?: number;
  sections?: Record<string, SectionMeta>;
}

/**
 * Format data age for display
 */
export function formatDataAge(ageSeconds: number | null | undefined): string {
  if (ageSeconds == null) return '';

  if (ageSeconds < 60) {
    return 'just now';
  } else if (ageSeconds < 3600) {
    const minutes = Math.floor(ageSeconds / 60);
    return `${minutes}m ago`;
  } else if (ageSeconds < 86400) {
    const hours = Math.floor(ageSeconds / 3600);
    return `${hours}h ago`;
  } else {
    const days = Math.floor(ageSeconds / 86400);
    return `${days}d ago`;
  }
}

/**
 * Check if data is stale based on section type
 */
export function isDataStale(
  section: DataSection,
  ageSeconds: number | null | undefined
): boolean {
  if (ageSeconds == null) return true;
  return ageSeconds > STALE_THRESHOLDS[section];
}

/**
 * Get stale indicator text for UI display
 */
export function getStaleIndicatorText(meta: SectionMeta | undefined): string | null {
  if (!meta) return null;

  if (meta.fetch_error) {
    return `Error loading - showing cached data`;
  }

  if (meta.is_stale && meta.data_age) {
    return `Updated ${meta.data_age}`;
  }

  if (meta.is_stale && meta.data_age_seconds != null) {
    return `Updated ${formatDataAge(meta.data_age_seconds)}`;
  }

  return null;
}

/**
 * Check if browser is online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

/**
 * Hook to detect online/offline status changes
 */
export function useOnlineStatus(
  onOnline?: () => void,
  onOffline?: () => void
): void {
  if (typeof window === 'undefined') return;

  const handleOnline = () => {
    if (onOnline) onOnline();
  };

  const handleOffline = () => {
    if (onOffline) onOffline();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Cleanup is handled by caller
}

/**
 * Calculate overall staleness for a response with multiple sections
 */
export function calculateOverallStaleness(
  meta: ResponseMeta | undefined
): { hasStale: boolean; staleSections: string[]; errors: string[] } {
  if (!meta?.sections) {
    return { hasStale: false, staleSections: [], errors: [] };
  }

  const staleSections: string[] = [];
  const errors: string[] = [];

  for (const [section, sectionMeta] of Object.entries(meta.sections)) {
    if (sectionMeta.is_stale) {
      staleSections.push(section);
    }
    if (sectionMeta.fetch_error) {
      errors.push(sectionMeta.fetch_error);
    }
  }

  return {
    hasStale: staleSections.length > 0,
    staleSections,
    errors,
  };
}

/**
 * IndexedDB storage keys for offline caching
 */
export const CACHE_KEYS = {
  PLAYER_PROFILE: (playerId: number) => `player_profile_${playerId}`,
  PLAYER_EXTENDED: (playerId: number) => `player_extended_${playerId}`,
} as const;

/**
 * Cache entry with timestamp
 */
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: number;
}

// Current cache version - increment when data structure changes
export const CACHE_VERSION = 1;
